import { getPlayerIdFromTypedId } from "../../util/IdUtils";
import { ClassBasedMutableDataMapper, mapperClassRecordToFnRecord } from "../../framework/layer/DataMapper";
import { REPLICATED_OBJECT_PATH } from "../../framework/firebase/DBPathConstants";
import { GameIdFirebaseWrapper } from "../../framework/firebase/GameIdFirebaseWrapper";
export class WriteableReplicatedObject {
    constructor(firebase, path, data, viewData, getWriterContext) {
        this.path = path;
        this.firebase = firebase;
        this._data = data;
        this._viewData = viewData;
        this.className = data.className;
        this.getWriterContext = getWriterContext;
    }
    get data() {
        return this._viewData;
    }
    get rawData() {
        return this._data;
    }
    get context() {
        return this.getWriterContext();
    }
    /**
     * Set data in response to database listener updates.
     * Don't call this to update the object, call write or delete.
     */
    _setData(data, viewData) {
        this._data = data;
        this._viewData = viewData;
    }
    /**
     * Overwrite the existing data. All fields are required.
     */
    overwrite(inData) {
        const data = Object.assign(Object.assign({}, inData), { id: this._data.id, className: this._data.className });
        this.firebase.set(this.path, data);
    }
    delete() {
        this.firebase.remove(this.path);
    }
    getOwnerId() {
        return getPlayerIdFromTypedId(this.data.id);
    }
    /**
     * Relay an update to the networking class, which sends it to the database.
     *
     * Actions should call this method to update ReplicatedObjects when committed on the host.
     *
     * Do not override this - override `transformDataToCommit`, `beforeCommitData`, and `afterCommitData` instead.
     */
    commitData(partialData, deleteKeyProp) {
        this.beforeCommitData(partialData);
        const data = this.transformDataToCommit({ partialData, previousData: this._data, deleteKeyProp });
        this.overwrite(data);
    }
    /**
     * Combine the new partial data to commit with the existing data.
     *
     * Override this to change how the data is transformed as it's committed.
     */
    transformDataToCommit(dataInput) {
        var _a;
        if (this.isInitialData(dataInput)) {
            return dataInput.initialData;
        }
        else {
            const result = Object.assign(Object.assign({}, dataInput.previousData), dataInput.partialData);
            if (((_a = dataInput.deleteKeyProp) === null || _a === void 0 ? void 0 : _a.deleteKeys) !== undefined) {
                for (const deleteKey of dataInput.deleteKeyProp.deleteKeys) {
                    delete result[deleteKey];
                }
            }
            return result;
        }
    }
    isInitialData(dataInput) {
        return 'initialData' in dataInput;
    }
    /**
     * Called on the server before a replicatedObject is updated from CommitData. This method is not called on construct.
     *
     * this.data = old data. partialData is the untransformed data.
     *
     * CommitData -> beforeCommitData -> transformDataToCommit -> <Object created/updated on the network> -> onSetDataLocal -> afterCommitData
     *
     * Override this to add additional effects when data is committed on the host.
     */
    beforeCommitData(partialData) { }
    /**
     * Called on the server after a replicatedObject is created or updated. This method is called on construct.
     *
     * this.data = new, transformed data. oldData = data from before commitData, null if object is newly created.
     *
     * Construct flow: CreateReplicatedObject -> transformDataToCommit -> <Object created on the network> -> onSetDataLocal -> afterCommitData
     */
    afterCommitData(oldData) { }
    /**
     * Delete this object on the network.
     *
     * Actions should call this method to delete replicated objects.
     *
     * Do not override this - override `beforeCommitDelete` or `afterCommitDelete` instead.
     */
    commitDelete() {
        this.beforeCommitDelete();
        this.delete();
        this.afterCommitDelete();
    }
    /**
     * Additional effects before this object is deleted on the host.
     *
     * Override this method to define additional deletion behaviors, such as deleting other replicated objects.
     */
    beforeCommitDelete() { }
    /**
     * Additional effects after this object is deleted on the host.
     *
     * Override this method to define additional deletion behaviors, such as deleting other replicated objects.
     */
    afterCommitDelete() { }
}
/**
 * Converts a map of string to WriteableReplicatedObject class into a standard BuilderMap for dataMapper
 * (Contains fns instead of class constructors, and those fns take less arguments)
 */
function getWriteableReplicatedObjectBuilderMap(getFirebase, inMap, getWriterContext, getRawData) {
    const fnRecord = mapperClassRecordToFnRecord(inMap);
    const output = Object.create(null);
    for (const key of Object.keys(inMap)) {
        const typedKey = key;
        output[typedKey] = (key, input, oldData) => fnRecord[typedKey](getFirebase(), `${REPLICATED_OBJECT_PATH}/${input.id}`, getRawData(key), input, getWriterContext);
    }
    return output;
}
export class WriterContextProvider extends ClassBasedMutableDataMapper {
    constructor(firebase, builderMap, gameContext, replicatedObjectCreator, lobbyDataWriter, replicatedTimeoutManager, replicatedObjectDataProvider, replicatedObjectDataListener, onCommitEndGame) {
        const convertedBuilderMap = getWriteableReplicatedObjectBuilderMap(() => this.gameIdFirebase, builderMap, () => this.getWriterContext(), (key) => replicatedObjectDataListener.data[key]);
        super(convertedBuilderMap, replicatedObjectDataProvider);
        this.replicatedObjectsById = Object.create(null);
        this.writerContext = null;
        this.gameIdFirebase = null;
        this.gameContext = gameContext;
        this.replicatedObjectCreator = replicatedObjectCreator;
        this.lobbyDataWriter = lobbyDataWriter;
        this.replicatedTimeoutManager = replicatedTimeoutManager;
        this.replicatedObjectDataListener = replicatedObjectDataListener;
        this.onCommitEndGame = onCommitEndGame;
        this.firebase = firebase;
    }
    activate() {
        this.gameIdFirebase = new GameIdFirebaseWrapper(this.firebase, this.gameContext.data.gameId);
        super.activate();
        this.replicatedTimeoutManager.initTimeouts(() => this.getWriterContext());
    }
    onActivateAfterCreateObjects() {
        this.writerContext = this.generateWriterContext();
    }
    createData(key, data, oldData) {
        const result = super.createData(key, data, oldData);
        this.replicatedObjectsById[data.id] = result;
        return result;
    }
    updateData(key, data, oldData) {
        this.data[key]._setData(this.replicatedObjectDataListener.data[key], data);
        this.data[key].afterCommitData(oldData);
    }
    onDeleteData(key, oldData) {
        delete this.replicatedObjectsById[oldData.id];
    }
    update(inEvent) {
        super.update(inEvent);
        this.writerContext = this.generateWriterContext();
        return null;
    }
    generateWriterContext() {
        const gameContextData = this.gameContext.data;
        const createReplicatedObject = (className, dataInput, ownerId) => this.replicatedObjectCreator.createReplicatedObject(className, dataInput, ownerId);
        const createReplicatedTimeout = (className, data, dependencyReplicatedObjectIds) => this.replicatedTimeoutManager.createReplicatedTimeout(className, data, dependencyReplicatedObjectIds, gameContextData.gameId, () => this.getWriterContext());
        const commitEndGame = (lastGameSummary) => {
            if (gameContextData.gameLifecycleStage !== 'Ingame') {
                return;
            }
            this.onCommitEndGame(() => this.getWriterContext());
            this.replicatedTimeoutManager.clearAllTimeouts();
            this.lobbyDataWriter.writeGameLifecycleStage("Lobby");
            this.lobbyDataWriter.writeLastGameSummary(lastGameSummary);
        };
        return Object.assign(Object.assign(Object.assign({}, gameContextData), this.data), { createReplicatedObject, getReplicatedObjectById: (replicatedObjectId) => this.replicatedObjectsById[replicatedObjectId], createReplicatedTimeout, setGameInitialized: (input) => this.lobbyDataWriter.writeGameInitialized(input), commitEndGame, setGameLifecycleStage: (input) => this.lobbyDataWriter.writeGameLifecycleStage(input) });
    }
    getWriterContext() {
        return this.writerContext;
    }
}
//# sourceMappingURL=WriterContext.js.map